import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import nextlaneApi from '../services/nextlane';
import './NextlaneReportingAtelier.css';

const fmt = (n) => n != null ? Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €' : '—';
const fmtPct = (n) => n != null ? Number(n).toFixed(1) + ' %' : '—';
const fmtQty = (n) => n != null ? Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) : '—';

const gaugeColor = (pct) => pct >= 45 ? 'var(--color-success)' : pct >= 30 ? 'var(--color-warning)' : 'var(--color-error)';

const PERIODS = [
  { id: 'today', label: "Aujourd'hui", days: 0 },
  { id: 'yesterday', label: 'Hier', days: -1 },
  { id: 'week', label: 'Semaine', days: 7 },
  { id: 'month', label: 'Mois', days: 30 },
  { id: '3m', label: '3 mois', days: 90 },
  { id: '6m', label: '6 mois', days: 180 },
  { id: '12m', label: '12 mois', days: 365 },
];

function getDateRange(periodId) {
  const now = new Date();
  const to = now.toISOString().split('T')[0];
  if (periodId === 'today') return { from: to, to };
  if (periodId === 'yesterday') {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    const yd = y.toISOString().split('T')[0];
    return { from: yd, to: yd };
  }
  const p = PERIODS.find(x => x.id === periodId);
  if (!p) return { from: to, to };
  const d = new Date(now); d.setDate(d.getDate() - p.days);
  return { from: d.toISOString().split('T')[0], to };
}

function NextlaneReportingAtelier({ user, setHeaderAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('3m');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [dealerships, setDealerships] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filtreDealership, setFiltreDealership] = useState('');
  const [filtreDept, setFiltreDept] = useState('');
  const [filtrePayer, setFiltrePayer] = useState('');

  useEffect(() => {
    nextlaneApi.getInvoiceDealerships().then(r => setDealerships(r.data)).catch(() => {});
    nextlaneApi.getInvoiceDepartments().then(r => setDepartments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (setHeaderAction) setHeaderAction(null);
  }, [setHeaderAction]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const range = period === 'custom' ? { from: customFrom, to: customTo } : getDateRange(period);
      const params = { ...range };
      if (filtreDealership) params.dealership = filtreDealership;
      if (filtreDept) params.department = filtreDept;
      if (filtrePayer) params.payer = filtrePayer;
      const { data } = await nextlaneApi.getInvoiceKpis(params);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [period, customFrom, customTo, filtreDealership, filtreDept, filtrePayer]);

  useEffect(() => { loadData(); }, [loadData]);

  const s = data?.summary || {};
  const n1 = s.n1 || {};
  const tauxMargePieces = parseFloat(s.taux_marge_pieces) || 0;
  const tauxMargeTotal = parseFloat(s.taux_marge_total) || 0;

  const delta = (current, prev) => {
    if (!prev || prev === 0) return null;
    return ((current - prev) / Math.abs(prev)) * 100;
  };

  const DeltaBadge = ({ current, prev }) => {
    const d = delta(parseFloat(current) || 0, parseFloat(prev) || 0);
    if (d === null) return null;
    const color = d >= 0 ? 'var(--color-success)' : 'var(--color-error)';
    const arrow = d >= 0 ? '▲' : '▼';
    return (
      <span style={{ fontSize: '0.72rem', fontWeight: 600, color, marginLeft: 6 }}>
        {arrow} {Math.abs(d).toFixed(1)}% <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>vs N-1</span>
      </span>
    );
  };

  return (
    <div className="page-container">
      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="nra-period-btns">
          {PERIODS.map(p => (
            <button key={p.id} className={`nra-period-btn ${period === p.id ? 'active' : ''}`} onClick={() => setPeriod(p.id)}>{p.label}</button>
          ))}
          <button className={`nra-period-btn ${period === 'custom' ? 'active' : ''}`} onClick={() => setPeriod('custom')}>Custom</button>
        </div>
        {period === 'custom' && (
          <>
            <input type="date" className="page-select" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
            <input type="date" className="page-select" value={customTo} onChange={e => setCustomTo(e.target.value)} />
          </>
        )}
        <select className={`page-select${filtreDealership ? ' active-filter' : ''}`} value={filtreDealership} onChange={e => setFiltreDealership(e.target.value)}>
          <option value="">Toutes concessions</option>
          {dealerships.map(d => <option key={d.dealership_id} value={d.dealership_id}>{d.dealership_name}</option>)}
        </select>
        <select className={`page-select${filtreDept ? ' active-filter' : ''}`} value={filtreDept} onChange={e => setFiltreDept(e.target.value)}>
          <option value="">Tous départements</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className={`page-select${filtrePayer ? ' active-filter' : ''}`} value={filtrePayer} onChange={e => setFiltrePayer(e.target.value)}>
          <option value="">Tous payeurs</option>
          <option value="Client">Client</option>
          <option value="Interne">Interne</option>
          <option value="Garantie">Garantie</option>
        </select>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>}

      {!loading && data && (
        <>
          {/* KPI Cards */}
          {(() => {
            const showN1 = ['month','3m','6m','12m','custom'].includes(period) && n1.ca_total > 0;
            return (
              <div className="nra-kpi-grid">
                <div className="nra-kpi-card">
                  <div className="nra-kpi-label">CA Total HT</div>
                  <div className="nra-kpi-value">{fmt(s.ca_total)}</div>
                  <div className="nra-kpi-sub">{s.nb_factures} facture{s.nb_factures > 1 ? 's' : ''}</div>
                  {showN1 && <div className="nra-kpi-n1"><DeltaBadge current={s.ca_total} prev={n1.ca_total} /></div>}
                </div>
                <div className="nra-kpi-card">
                  <div className="nra-kpi-label">CA Main d'Oeuvre</div>
                  <div className="nra-kpi-value">{fmt(s.ca_mo)}</div>
                  <div className="nra-kpi-sub">{s.ca_total > 0 ? (s.ca_mo / s.ca_total * 100).toFixed(0) : 0}% du CA</div>
                  {showN1 && <div className="nra-kpi-n1"><DeltaBadge current={s.ca_mo} prev={n1.ca_mo} /></div>}
                </div>
                <div className="nra-kpi-card">
                  <div className="nra-kpi-label">CA Pièces</div>
                  <div className="nra-kpi-value">{fmt(s.ca_pieces)}</div>
                  <div className="nra-kpi-sub">Coût : {fmt(s.cout_pieces)}</div>
                  {showN1 && <div className="nra-kpi-n1"><DeltaBadge current={s.ca_pieces} prev={n1.ca_pieces} /></div>}
                </div>
                <div className="nra-kpi-card">
                  <div className="nra-kpi-label">Marge Pièces</div>
                  <div className="nra-kpi-value" style={{ color: gaugeColor(tauxMargePieces) }}>{fmt(s.marge_pieces)}</div>
                  <div className="nra-kpi-sub" style={{ fontWeight: 600, color: gaugeColor(tauxMargePieces) }}>{fmtPct(tauxMargePieces)}</div>
                  <div className="nra-gauge"><div className="nra-gauge-fill" style={{ width: `${Math.min(tauxMargePieces, 100)}%`, background: gaugeColor(tauxMargePieces) }} /></div>
                  {showN1 && <div className="nra-kpi-n1"><DeltaBadge current={s.marge_pieces} prev={n1.marge_pieces} /></div>}
                </div>
                <div className="nra-kpi-card">
                  <div className="nra-kpi-label">Ticket Moyen</div>
                  <div className="nra-kpi-value">{fmt(s.ticket_moyen)}</div>
                  {showN1 && <div className="nra-kpi-n1"><DeltaBadge current={s.ticket_moyen} prev={n1.ticket_moyen} /></div>}
                </div>
                <div className="nra-kpi-card">
                  <div className="nra-kpi-label">Marge Totale</div>
                  <div className="nra-kpi-value" style={{ color: gaugeColor(tauxMargeTotal) }}>{fmt(s.marge_totale)}</div>
                  <div className="nra-kpi-sub" style={{ fontWeight: 600, color: gaugeColor(tauxMargeTotal) }}>{fmtPct(tauxMargeTotal)}</div>
                  <div className="nra-gauge"><div className="nra-gauge-fill" style={{ width: `${Math.min(tauxMargeTotal, 100)}%`, background: gaugeColor(tauxMargeTotal) }} /></div>
                  {showN1 && <div className="nra-kpi-n1"><DeltaBadge current={s.marge_totale} prev={n1.marge_totale} /></div>}
                </div>
              </div>
            );
          })()}

          {/* Graphique évolution mensuelle */}
          {(data.parMois || []).length > 1 && (
            <div className="nra-chart-card">
              <div className="nra-chart-title">Évolution mensuelle</div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.parMois} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
                  <XAxis dataKey="mois" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                  <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} width={50} />
                  <Tooltip formatter={(v, name) => [fmt(v), name]} labelStyle={{ fontWeight: 600 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line dataKey="ca_mo" name="CA MO" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  <Line dataKey="ca_pieces" name="CA Pièces" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line dataKey="marge_pieces" name="Marge Pièces" stroke="#f97316" strokeWidth={2} strokeDasharray="5 3" dot={false} />
                  <Line dataKey="ca_total" name="CA Total" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Répartition par payeur */}
          {(data.parPayeur || []).length > 0 && (
            <div className="nra-tables-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="nra-table-card">
                <div className="nra-table-header">Répartition par payeur</div>
                <table>
                  <thead><tr><th>Payeur</th><th className="right">CA HT</th><th className="right">% CA</th><th className="right">Factures</th></tr></thead>
                  <tbody>
                    {data.parPayeur.map(p => (
                      <tr key={p.payer}>
                        <td style={{ fontWeight: 600 }}>{p.payer}</td>
                        <td className="right">{fmt(p.ca_total)}</td>
                        <td className="right">{s.ca_total > 0 ? (p.ca_total / s.ca_total * 100).toFixed(1) + ' %' : '—'}</td>
                        <td className="right">{p.nb_factures}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tableaux de ventilation */}
          <div className="nra-tables-grid">
            {/* Par département */}
            <div className="nra-table-card">
              <div className="nra-table-header">Par département</div>
              <table>
                <thead><tr><th>Département</th><th className="right">CA MO</th><th className="right">CA Pièces</th><th className="right">Marge</th><th className="right">Taux</th><th className="right">Total</th></tr></thead>
                <tbody>
                  {(data.parDepartement || []).map(d => {
                    const taux = d.ca_pieces > 0 ? (d.marge_pieces / d.ca_pieces * 100) : 0;
                    return (
                      <tr key={d.department_name}>
                        <td style={{ fontWeight: 600 }}>{d.department_name}</td>
                        <td className="right">{fmt(d.ca_mo)}</td>
                        <td className="right">{fmt(d.ca_pieces)}</td>
                        <td className="right">{fmt(d.marge_pieces)}</td>
                        <td className="right"><span className="nra-margin-badge" style={{ background: gaugeColor(taux) + '22', color: gaugeColor(taux) }}>{fmtPct(taux)}</span></td>
                        <td className="right" style={{ fontWeight: 600 }}>{fmt(d.ca_total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </div>

          {/* Top prestations MO + Top pièces côte à côte */}
          <div className="nra-tables-grid">
            {(data.topPrestations || []).length > 0 && (
              <div className="nra-table-card">
                <div className="nra-table-header">Top prestations (MO)</div>
                <table>
                  <thead><tr><th>Prestation</th><th className="right">CA HT</th><th className="right">Nb</th></tr></thead>
                  <tbody>
                    {data.topPrestations.map((p, i) => (
                      <tr key={i}>
                        <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.item_descr}</td>
                        <td className="right" style={{ fontWeight: 600 }}>{fmt(p.ca_ht)}</td>
                        <td className="right">{p.nb_lignes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(data.topPieces || []).length > 0 && (
              <div className="nra-table-card">
                <div className="nra-table-header">Top 20 pièces (par CA)</div>
                <table>
                  <thead><tr><th>Pièce</th><th className="right">Qté</th><th className="right">CA</th><th className="right">Marge</th><th className="right">Taux</th></tr></thead>
                  <tbody>
                    {data.topPieces.map((p, i) => {
                      const taux = p.ca_vente > 0 ? (p.marge / p.ca_vente * 100) : 0;
                      return (
                        <tr key={i}>
                          <td style={{ maxWidth: 220 }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.item_descr}</div>
                            {p.item_ref && <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{p.item_ref}</div>}
                          </td>
                          <td className="right">{fmtQty(p.qty_total)}</td>
                          <td className="right">{fmt(p.ca_vente)}</td>
                          <td className="right" style={{ fontWeight: 600 }}>{fmt(p.marge)}</td>
                          <td className="right"><span className="nra-margin-badge" style={{ background: gaugeColor(taux) + '22', color: gaugeColor(taux) }}>{fmtPct(taux)}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {s.derniere_sync && (
            <div style={{ textAlign: 'right', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 8 }}>
              Dernière synchronisation : {new Date(s.derniere_sync).toLocaleString('fr-FR')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default NextlaneReportingAtelier;
